import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#observeDeferredSlides();
    this.#observeDeferredThumbnails();
    this.#preloadSlidesFromThumbnailControls();
  }

  #thumbnailObserver;

  #observeDeferredThumbnails() {
    const images = this.querySelectorAll('img[data-deferred-product-thumbnail]');
    if (!images.length) return;

    const hydrate = (image) => {
      if (!(image instanceof HTMLImageElement)) return;
      const srcset = image.dataset.srcset;
      const src = image.dataset.src;
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
      image.removeAttribute('data-deferred-product-thumbnail');
      this.#thumbnailObserver?.unobserve(image);
    };

    if (!('IntersectionObserver' in window)) {
      images.forEach(hydrate);
      return;
    }

    this.#thumbnailObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) hydrate(entry.target);
        }
      },
      { rootMargin: '120px' },
    );

    images.forEach((image) => this.#thumbnailObserver.observe(image));
  }

  #slideObserver = new MutationObserver(() => this.#hydrateActiveSlides());

  #observeDeferredSlides() {
    const slides = this.querySelectorAll('slideshow-slide');
    for (const slide of slides) {
      this.#slideObserver.observe(slide, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }
    this.#hydrateActiveSlides();
  }

  #preloadSlidesFromThumbnailControls() {
    const slides = [...this.querySelectorAll('slideshow-component slideshow-slide')];
    if (!slides.length) return;

    this.querySelector('slideshow-slides')?.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      const currentSlides = this.slideshow?.slides || slides;
      const index = currentSlides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
      if (index < 0) return;
      this.#hydrateSlide(currentSlides[index - 1]);
      this.#hydrateSlide(currentSlides[index + 1]);
    }, { passive: true, signal: this.#controller.signal });

    this.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail, index) => {
      const slide = slides[index % slides.length];
      if (!(slide instanceof HTMLElement)) return;

      const hydrate = () => this.#hydrateSlide(slide);
      thumbnail.addEventListener('pointerdown', hydrate, { capture: true, signal: this.#controller.signal });
      thumbnail.addEventListener('focusin', hydrate, { capture: true, signal: this.#controller.signal });
      thumbnail.addEventListener('click', hydrate, { capture: true, signal: this.#controller.signal });
    });
  }

  #hydrateActiveSlides() {
    const slides = [...this.querySelectorAll('slideshow-component slideshow-slide')];
    for (const slide of slides) {
      if (slide.getAttribute('aria-hidden') !== 'false') {
        slide.querySelectorAll('video').forEach((video) => video.pause());
        continue;
      }

      this.#hydrateSlide(slide);

      if (!window.matchMedia('(max-width: 749px)').matches) {
        this.#hydrateSlide(slides[slides.indexOf(slide) + 1]);
      }
    }
  }

  /**
   * @param {Element | undefined} slide
   */
  #hydrateSlide(slide) {
    if (!(slide instanceof HTMLElement)) return;

    for (const image of slide.querySelectorAll('img[data-deferred-product-image]')) {
      if (!(image instanceof HTMLImageElement)) continue;

      const srcset = image.dataset.srcset;
      const src = image.dataset.src;
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
      image.removeAttribute('data-deferred-product-image');
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#slideObserver.disconnect();
    this.#thumbnailObserver?.disconnect();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
